import * as Sentry from "@sentry/react";
import { v4 as uuidv4 } from "uuid";
import type { ApiErrorResult } from "_shared/_errors/api-error";

const reportedApiOperations = new Set<string>();
const reportedSchemaOperations = new Set<string>();
const reportedUnreadableRecords = new Set<string>();

/** Reports only bounded contract metadata. Request/response values and error text are never sent. */
export function reportApiError(error: ApiErrorResult, operation: string): void {
  if (error.category !== "unexpected" || error.status !== 400 || !error.traceId) return;

  const key = `${error.traceId}:${operation}`;
  if (reportedApiOperations.has(key)) return;
  reportedApiOperations.add(key);

  captureSanitizedMessage("Unexpected API 400", {
    tags: {
      operation,
      status: String(error.status),
      code: error.code ?? "unknown",
      traceId: error.traceId,
    },
  });
}

export type SchemaFailureReport = {
  schemaName: string;
  operation: string;
  phase: string;
  fields: readonly string[];
};

export function reportSchemaFailure(report: SchemaFailureReport): void {
  const fields = [...new Set(report.fields)].sort().join(",") || "unknown";
  const key = `${report.schemaName}:${report.operation}:${report.phase}:${fields}`;
  if (reportedSchemaOperations.has(key)) return;
  reportedSchemaOperations.add(key);

  captureSanitizedMessage("Client schema contract mismatch", {
    tags: {
      schema: report.schemaName,
      operation: report.operation,
      phase: report.phase,
    },
    extra: { fields },
  });
}

export type UnreadableRecordReport = {
  /** The kind of record, such as "Message". */
  record: string;
  operation: string;
  /** The record's id, when the payload carried one. */
  id?: string;
  /** The id of what holds the record, such as a message's forum. */
  parentId?: string;
};

/**
 * Reports a record that a list left out because it did not parse. Once per record, so a list read again
 * does not report it again. Ids only, never the payload.
 */
export function reportUnreadableRecord(report: UnreadableRecordReport): void {
  const key = `${report.record}:${report.operation}:${report.id ?? "unknown"}`;
  if (reportedUnreadableRecords.has(key)) return;
  reportedUnreadableRecords.add(key);

  captureSanitizedMessage("Client dropped an unreadable record", {
    tags: {
      record: report.record,
      operation: report.operation,
    },
    extra: {
      id: report.id ?? "unknown",
      parentId: report.parentId ?? "unknown",
    },
  });
}

/** The Sentry message of every "support has been notified" report. The support alert matches on it. */
export const SHOWN_ERROR_MESSAGE = "User was shown an error and told support was notified";

/** What a "support has been notified" snackbar reports. Bounded metadata only, never the error's text. */
export type ShownErrorReport = {
  operation: string;
  /** `server` for an answer the API sent, `client` for a failure in the browser. */
  origin: "server" | "client";
  status?: number;
  code?: string;
  traceId?: string;
  /** The thrown value's constructor name, such as `ZodError`, for a client failure. */
  errorName?: string;
};

/** How many characters of a trace or event id a person is shown: short enough to read out. */
const SUPPORT_REFERENCE_LENGTH = 8;

/**
 * Reports an error the person was told support had been notified of, and returns the reference to
 * show them: the start of the API's trace id when there is one, otherwise the start of the Sentry
 * event id.
 *
 * The event carries that reference as its `reference` tag. Sentry refuses a wildcard on an event id
 * or a trace, so the eight characters a person quotes find the event only through the tag (#7071).
 *
 * Every call is reported, not once per operation: each one is a person who saw the error (#6614).
 */
export function reportShownError(report: ShownErrorReport): string {
  // The event id is chosen here, so the reference is known before the event is sent.
  const eventId = uuidv4().replaceAll("-", "");
  const reference = (report.traceId ?? eventId).slice(0, SUPPORT_REFERENCE_LENGTH);
  captureSanitizedMessage(
    SHOWN_ERROR_MESSAGE,
    {
      tags: {
        operation: report.operation,
        origin: report.origin,
        status: report.status === undefined ? "none" : String(report.status),
        code: report.code ?? "unknown",
        traceId: report.traceId ?? "none",
        errorName: report.errorName ?? "none",
        reference,
      },
    },
    "error",
    eventId,
  );
  return reference;
}

export function resetClientErrorReports(): void {
  reportedApiOperations.clear();
  reportedSchemaOperations.clear();
  reportedUnreadableRecords.clear();
}

type SanitizedTelemetry = {
  tags: Record<string, string>;
  extra?: Record<string, string>;
};

/**
 * These reports can be raised after a failed fetch, when the active Sentry scope can contain request
 * breadcrumbs and unrelated page context. Replace the fully prepared event, after all SDK processors
 * have run, so only this module's bounded metadata reaches the transport.
 */
function captureSanitizedMessage(
  message: string,
  telemetry: SanitizedTelemetry,
  level: "warning" | "error" = "warning",
  eventId?: string,
): string {
  return Sentry.withScope((scope) => {
    scope.addEventProcessor((event) => ({
      event_id: event.event_id,
      timestamp: event.timestamp,
      message,
      level,
      tags: telemetry.tags,
      ...(telemetry.extra ? { extra: telemetry.extra } : {}),
    }));
    // The processor above supplies the tags and extras. An absent id leaves Sentry to choose one.
    return scope.captureMessage(message, level, { event_id: eventId });
  });
}
